import { Plus } from 'lucide-react';
import { CqgConnectorDialog } from '@features/cqg';
import { RithmicConnectDialog } from '@features/rithmic';
import { AccountConnectionControl } from '@features/accounts/components/AccountConnectionControl';
import { AccountsPanel } from '@features/accounts/components/AccountsPanel';
import { AddAccountDialog } from '@features/accounts/components/AddAccountDialog';
import { useConnectDialogStore } from '@features/accounts/state/connectDialogStore';
import { useAccounts } from '@features/trades/hooks/useTradeMetadata';
import { RithmicAttribution } from '@features/rithmic/components/RithmicAttribution';
import { useRithmicAccountSelected } from '@features/rithmic/hooks/useRithmicAccountSelected';
import { PlanMyDayButton } from '@features/day-journal/components/PlanMyDayButton';
import { featurePlanMyDay } from '@config/env';
import { usePageHeaderStore } from '@shared/state/pageHeaderStore';
import { useSeedDateRangeFromFirstTrade } from '@shared/state/useSeedDateRangeFromFirstTrade';
import { BRAND_GRADIENT } from '@shared/styles/brand';
import { RecalculatingIndicator } from './RecalculatingIndicator';
import { DateRangeTrigger } from './DateRangeTrigger';
import { LastImportStamp } from './LastImportStamp';
import { TOPBAR_HEIGHT_PX } from './layoutMetrics';

export { TOPBAR_HEIGHT_PX };

/**
 * The app's single top header (2026-07 Figma redesign: 48px bar). Left: the current page's injected
 * `title` from `pageHeaderStore` (heading-md 20/24). Right: the "Last update" stamp (+ gradient
 * refresh glyph inside it), page `extras`, the recalculating hint, then the GLOBAL controls — the
 * unified {@link AccountConnectionControl} (status dot + state chip + selected account + chevron;
 * replaces the old AccountDropdown AND both provider state badges) and the date-range pill.
 *
 * The CQG/Rithmic connector dialogs stay PERMANENTLY MOUNTED here (closed) — they own the relay
 * lifecycle, auto-resume, and reconnect-handler effects, which run regardless of `open`. Their
 * open-state now lives in `connectDialogStore` so the Accounts panel's Link buttons and the
 * Add-account dialog's take-over handoff can open them from anywhere.
 */
export function Topbar() {
  const title = usePageHeaderStore(s => s.title);
  const subtitle = usePageHeaderStore(s => s.subtitle);
  const extras = usePageHeaderStore(s => s.extras);
  const openAddAccount = useConnectDialogStore(s => s.openAddAccount);
  // No-accounts onboarding state (2026-07-24): a fresh user (empty accounts list) has nothing to
  // select, connect, or scope by date — so the global controls collapse to a single prominent
  // "Connect Trading Account" CTA (the 2026-09-02 Figma's wording; it read "Add new account" before)
  // and the date-range pill is hidden. Once they add one, the normal controls return.
  // `isSuccess && length === 0` (not just `!length`) so the CTA doesn't flash during load.
  const accounts = useAccounts();
  const noAccounts = accounts.isSuccess && accounts.data.length === 0;
  // Rithmic conformance: their marks render under the page title while a Rithmic account is selected
  // (same gate as the footer, shared via the hook so the two can't drift).
  const rithmicSelected = useRithmicAccountSelected();
  const cqgOpen = useConnectDialogStore(s => s.cqgOpen);
  const rithmicOpen = useConnectDialogStore(s => s.rithmicOpen);
  const closeCqg = useConnectDialogStore(s => s.closeCqg);
  const closeRithmic = useConnectDialogStore(s => s.closeRithmic);
  // One-shot row-driven hint (Accounts panel → Connect): preselect the CQG dialog's Demo/Live tab
  // + prefill the account's remembered username.
  const cqgHint = useConnectDialogStore(s => s.cqgHint);
  const clearCqgHint = useConnectDialogStore(s => s.clearCqgHint);
  // Same one-shot row-driven hint for Rithmic: preselect the system dropdown + prefill the username.
  const rithmicHint = useConnectDialogStore(s => s.rithmicHint);
  const clearRithmicHint = useConnectDialogStore(s => s.clearRithmicHint);
  // Anchor the DEFAULT global date range's START to the account's first-trade day (END = today) — so a
  // fresh visitor sees their whole history. No-op once the user picks a range. Mounted here because the
  // Topbar is always present and inside the data providers.
  useSeedDateRangeFromFirstTrade();

  return (
    <header
      className="flex shrink-0 items-center gap-4 bg-canvas-raised px-4"
      style={{ height: TOPBAR_HEIGHT_PX, borderBottom: '1px solid var(--canvas-divider)' }}
    >
      {title && (
        <div className="flex min-w-0 flex-col justify-center">
          <h1 className="font-display text-xl font-semibold tracking-tight text-text">{title}</h1>
          {/* Page subtitle (2026-07-27). 12px and truncated: the bar is a fixed 48px, so the
              description has to sit inside the remaining ~20px under a 24px title line. */}
          {subtitle && (
            <p className="truncate text-[12px] leading-4 text-text-muted">{subtitle}</p>
          )}
          {/* Rithmic conformance (user direction 2026-07-24): their two marks sit directly UNDER the
              page title, ONLY while a Rithmic account is selected — so the logos are on screen on
              every page whenever the user is scoped to Rithmic data. `sm` (h-3.5) + the capped width
              keeps the pair inside the 48px bar under the 24px title line. */}
          {rithmicSelected && (
            <RithmicAttribution size="sm" className="mt-0.5 max-w-[240px]" />
          )}
        </div>
      )}

      <div className="ml-auto flex min-w-0 items-center gap-3 text-sm">
        {/* GLOBAL "Last update" freshness stamp (with the Figma gradient refresh glyph inside it —
            both hide together below md and when there's no freshness source yet). */}
        <LastImportStamp />
        {extras}
        <RecalculatingIndicator />
        {/* Actions (Figma gap-8): the unified account/connection control + the date-range pill.
            Flipping either re-keys every scoped query. When the user has no accounts yet, both are
            replaced by a single "Connect Trading Account" CTA (nothing to select or scope until then). */}
        <div className="flex items-center gap-2">
          {/* Plan My Day (spec §1) leads the actions group: it is the day's entry point, so it stays
              reachable from every page — including the no-accounts onboarding state, where planning
              the day is one of the few useful things a fresh user can do. */}
          {featurePlanMyDay && <PlanMyDayButton />}
          {noAccounts ? (
            <button
              type="button"
              onClick={openAddAccount}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: BRAND_GRADIENT }}
            >
              <Plus className="h-4 w-4" aria-hidden />
              Connect Trading Account
            </button>
          ) : (
            <>
              <AccountConnectionControl />
              <DateRangeTrigger />
            </>
          )}
        </div>
      </div>

      {/* Account-connection surfaces. The two connector dialogs must stay mounted (relay lifecycle —
          see the component doc above); the panel + add dialog mount/unmount on open like any modal. */}
      <AccountsPanel />
      <AddAccountDialog />
      <CqgConnectorDialog
        open={cqgOpen}
        onClose={closeCqg}
        environmentHint={cqgHint?.environment}
        usernameHint={cqgHint?.username}
        onHintConsumed={clearCqgHint}
      />
      <RithmicConnectDialog
        open={rithmicOpen}
        onClose={closeRithmic}
        systemHint={rithmicHint?.system}
        usernameHint={rithmicHint?.username}
        onHintConsumed={clearRithmicHint}
      />
    </header>
  );
}
